import store from 'store';
import StoreGetters from 'store/store-getters';
import { sanitizeBuilding, getAllowedOrigins } from './utils';
import { FLOOR_VIEWER_SOURCE, FLOOR_VIEWER_MESSAGE_TYPES } from './schema';

const NAVIGATION_DEBOUNCE_MS = 150;

let lastSentSnapshot = null;
let pendingSnapshot = null;
let debounceTimer = null;

function buildNavigationSnapshot(state) {
  const {
    id,
    currentRenderIndex,
    currentHotSpot,
    viewButton,
    viewVariable,
    queryStringList,
  } = state;

  const floorId = StoreGetters.renderIndex2FloorId();

  return {
    buildingId: id || null,
    floorIndex:
      typeof currentRenderIndex === 'number' ? currentRenderIndex : null,
    floorId: floorId || null,
    viewState: viewButton ? viewButton.viewState : null,
    hotspotId: currentHotSpot || null,
    isCameraMoving: viewVariable ? viewVariable.isCameraMoving : null,
    query: queryStringList || {},
  };
}

function isSameSnapshot(a, b) {
  if (!a || !b) return false;
  return (
    a.buildingId === b.buildingId &&
    a.floorIndex === b.floorIndex &&
    a.floorId === b.floorId &&
    a.viewState === b.viewState &&
    a.hotspotId === b.hotspotId &&
    a.isCameraMoving === b.isCameraMoving &&
    JSON.stringify(a.query) === JSON.stringify(b.query)
  );
}

// Debounced handling Navigation snapshots changes:
function enqueueNavigation(snapshot) {
  if (pendingSnapshot && isSameSnapshot(pendingSnapshot, snapshot)) {
    return;
  }

  pendingSnapshot = snapshot;

  if (snapshot.isCameraMoving) {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }
    return;
  }

  if (debounceTimer) {
    clearTimeout(debounceTimer);
  }

  debounceTimer = setTimeout(() => {
    debounceTimer = null;

    const toSend = pendingSnapshot;
    pendingSnapshot = null;

    if (!toSend) return;

    if (lastSentSnapshot && isSameSnapshot(lastSentSnapshot, toSend)) {
      return;
    }

    safePostMessage({
      source: FLOOR_VIEWER_SOURCE,
      type: FLOOR_VIEWER_MESSAGE_TYPES.NAVIGATION,
      payload: toSend,
    });

    lastSentSnapshot = toSend;
  }, NAVIGATION_DEBOUNCE_MS);
}

function safePostMessage(message) {
  try {
    if (typeof window === 'undefined') return;
    // Not embedded in iframe
    if (window.parent === window) return;

    const allowedOrigins = getAllowedOrigins();

    let parentOrigin = null;
    try {
      if (document.referrer) {
        const url = new URL(document.referrer);
        parentOrigin = url.origin;
      }
    } catch (e) {
      // ignore referrer parsing errors
    }

    if (allowedOrigins.length > 0) {
      const target =
        parentOrigin && allowedOrigins.includes(parentOrigin)
          ? parentOrigin
          : null;

      if (!target) {
        return console.warn(
          'navigationBridge postMessage blocked, unallowed origin',
          parentOrigin
        );
      }
      window.parent.postMessage(message, target);
    } else {
      console.warn('navigationBridge postMessage no allowlist configured');
    }
  } catch (e) {
    console.warn('navigationBridge postMessage failed', e);
  }
}

function sendInitializedOnce() {
  const state = store.getState();
  const { id, building } = state;

  const payload = {
    buildingId: id || null,
    building: sanitizeBuilding(building),
  };

  safePostMessage({
    source: FLOOR_VIEWER_SOURCE,
    type: FLOOR_VIEWER_MESSAGE_TYPES.INITIALIZED,
    payload,
  });
}

export function attachOutBoundBridge() {
  sendInitializedOnce();

  const unsubscribe = store.subscribe(() => {
    const state = store.getState();
    const snapshot = buildNavigationSnapshot(state);

    enqueueNavigation(snapshot);
  });

  return unsubscribe;
}
